import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../lib/api";
import env from "../../env";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";
import { X, Loader2, Sparkles, Trash2, Download, Check, Star, Pencil, Crop } from "lucide-react";
import { useSupplierVariations } from "../../lib/useSupplierVariations";
import { useShopVertical } from "../../lib/useShopVertical";
import { authHeaders, getBackendUrl } from "../../lib/http";
import type { CustomMockup, MockupFolder } from "@erc/shared";
import { DesignAreaEditor, type AreaRect } from "./DesignAreaEditor";

const abs = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${env.API_URL}${u}`);

interface SourceList { id: string; sourceUrl: string | null; sourceTitle: string | null; imageUrls: string[]; createdAt: string }

// Eklentiden "Mockup Cek" ile gonderilen listeleri gosterir; secilen mockuplardan
// AI ile BOS mockup uretir (baski kaldirilir, tisort korunur) -> havuza kaydedilir.
export function MockupListsModal({ onClose, onCreated }: { onClose: () => void; onCreated?: () => void }) {
  const { models, model: effectiveModel, setModel } = useDesignModel("erc:model:blank-mockup");
  const [lists, setLists] = useState<SourceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [keepShirt, setKeepShirt] = useState(true);
  const [cleanBg, setCleanBg] = useState(false);
  const [onlyDesign, setOnlyDesign] = useState(true);
  const [extra, setExtra] = useState("");
  // Uretim ANINDA ad ve klasor: sonradan tek tek duzeltmek zaman aliyordu
  const [label, setLabel] = useState("");
  const [folderId, setFolderId] = useState("");
  const [folders, setFolders] = useState<MockupFolder[]>([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Array<{ source: string; imageUrl?: string; mockupId?: string; error?: string }>>([]);
  // Uretilen mockup'larin TAM kaydi — kart uzerinden duzenlenebilsin diye
  const [created, setCreated] = useState<CustomMockup[]>([]);
  const [productTypes, setProductTypes] = useState<Array<{ key: string; label: string }>>([]);
  // Uretilen mockup icin tasarim alani editoru (Mockup Yonetimi'ndeki ile ayni bilesen)
  const [areaMockup, setAreaMockup] = useState<CustomMockup | null>(null);
  // Marka/model/renk secicileri aktif dikeyin katalogundan
  const { vertical } = useShopVertical();
  const vars = useSupplierVariations(vertical);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.listMockupSources().then((d) => setLists(d as SourceList[])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiClient.listMockupFolders("mockup")
      .then((d) => setFolders((d as MockupFolder[]) ?? []))
      .catch(() => { /* klasor alinamazsa kok dizine kaydedilir */ });
  }, []);

  useEffect(() => {
    fetch(`${getBackendUrl()}/api/product-types`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((j) => setProductTypes(j.data ?? []))
      .catch(() => { /* liste alinamazsa urun tipi secici bos kalir */ });
  }, []);

  // Klasor agaci girintili liste (Mockup Yonetimi ile ayni gorunum)
  const folderOptions = (() => {
    const byParent: Record<string, MockupFolder[]> = {};
    folders.forEach((f) => { const k = f.parentId ?? "root"; (byParent[k] ||= []).push(f); });
    const out: { id: string; label: string }[] = [];
    const walk = (parent: string, depth: number) => {
      (byParent[parent] || []).forEach((f) => {
        out.push({ id: f.id, label: `${"— ".repeat(depth)}${f.name}` });
        walk(f.id, depth + 1);
      });
    };
    walk("root", 0);
    return out;
  })();

  const toggle = (u: string) => setSelected((prev) => { const n = new Set(prev); n.has(u) ? n.delete(u) : n.add(u); return n; });

  async function del(id: string) {
    await apiClient.deleteMockupSource(id).catch(() => {});
    load();
  }

  async function generate() {
    if (selected.size === 0 || !effectiveModel) return;
    setBusy(true); setResults([]); setCreated([]);
    try {
      const r = await apiClient.generateBlankMockups({
        imageUrls: Array.from(selected).slice(0, 12), model: effectiveModel,
        keepShirt, cleanBackground: cleanBg, onlyDesign, extraPrompt: extra || undefined,
        label: label.trim() || undefined,
        folderId: folderId || undefined,
      });
      setResults(r.results);
      if (r.created > 0) {
        onCreated?.();
        // Olusan mockup kayitlarini getir ki kart uzerinden duzenlenebilsinler
        const ids = new Set(r.results.map((x) => x.mockupId).filter(Boolean) as string[]);
        if (ids.size > 0) {
          try {
            const all = await apiClient.listMockups();
            setCreated((all as CustomMockup[]).filter((m) => ids.has(m.id)));
          } catch { /* cekilemezse sade gorunum kalir */ }
        }
      }
    } catch (e) {
      setResults([{ source: "", error: e instanceof Error ? e.message : "Hata" }]);
    } finally {
      setBusy(false);
    }
  }

  // Alan kaydi: Mockup Yonetimi ile ayni sema — ilk alan legacy tekil alanlara da yazilir
  async function saveAreas(mockupId: string, areas: AreaRect[]) {
    const first = areas[0] as (AreaRect & { theme?: "light" | "dark" }) | undefined;
    try {
      const updated = await apiClient.updateMockup(mockupId, {
        designAreas: areas,
        theme: first?.theme ?? "light",
        designAreaX: first ? Math.round(first.x) : 0,
        designAreaY: first ? Math.round(first.y) : 0,
        designAreaW: first ? Math.round(first.w) : 0,
        designAreaH: first ? Math.round(first.h) : 0,
        designAreaRotation: first ? Math.round(first.rotation || 0) : 0,
      });
      setCreated((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      onCreated?.();
    } catch (e) {
      alert("Alan kaydedilemedi: " + (e instanceof Error ? e.message : String(e)));
    }
    setAreaMockup(null);
  }

  async function download(url: string) {
    try {
      const res = await fetch(abs(url)); const blob = await res.blob();
      const a = document.createElement("a"); const u = URL.createObjectURL(blob);
      a.href = u; a.download = `bos-mockup.png`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch { window.open(abs(url), "_blank"); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Sparkles className="h-5 w-5 text-indigo-600" /> Mockup Listeleri → Boş Mockup Üret</h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-4 md:flex-row">
          {/* Sol: listeler + secim */}
          <div className="flex-1 min-w-0">
            <p className="mb-2 text-xs text-gray-500">Eklentiden "Mockup Çek" ile gönderdiğin görseller. Boş mockup üretmek istediklerini seç.</p>
            {loading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>
            ) : lists.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
                Henüz mockup listesi yok. Eklentide bir listing sayfasında "Mockup Çek" kullan.
              </div>
            ) : (
              <div className="space-y-4">
                {lists.map((l) => (
                  <div key={l.id} className="rounded-lg border border-gray-200 p-2">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="truncate text-xs font-medium text-gray-700" title={l.sourceTitle || l.sourceUrl || ""}>
                        {l.sourceTitle || l.sourceUrl || "Mockup listesi"} · {new Date(l.createdAt).toLocaleDateString("tr-TR")}
                      </span>
                      <button onClick={() => del(l.id)} className="text-gray-300 hover:text-red-500" title="Listeyi sil"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5">
                      {l.imageUrls.map((u) => {
                        const on = selected.has(u);
                        return (
                          <button key={u} onClick={() => toggle(u)}
                            className={`relative overflow-hidden rounded-md border-2 ${on ? "border-indigo-500" : "border-transparent"}`}>
                            <img src={u} alt="" loading="lazy" className="h-20 w-full object-cover" />
                            {on && <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-white"><Check className="h-3 w-3" /></span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sag: konsept + uret + sonuc */}
          <div className="w-full space-y-3 md:w-72">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="mb-2 text-xs font-semibold text-gray-600">Kayıt</p>
              <label className="mb-1 block text-[11px] text-gray-500">Mockup adı</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Boş Mockup"
                className="mb-2 w-full rounded border border-gray-200 px-2 py-1 text-xs"
              />
              <label className="mb-1 block text-[11px] text-gray-500">Klasör</label>
              <select
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs"
              >
                <option value="">Klasörsüz (kök)</option>
                {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <p className="mt-1 text-[10px] text-gray-400">
                Birden fazla üretilirse adın sonuna sıra eklenir (Ad 1, Ad 2…).
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <p className="mb-2 text-xs font-semibold text-gray-600">Konsept</p>
              <label className="mb-1.5 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked disabled /> Baskıyı/tasarımı kaldır</label>
              {/* Model, "yaziyi temizle" talimatini genis yorumlayip marka etiketini ve
                  renk adi yazisini da siliyordu — bu secenek sadece baskiyi kaldirtir. */}
              <label className="mb-1.5 flex items-start gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={onlyDesign} onChange={(e) => setOnlyDesign(e.target.checked)} className="mt-0.5" />
                <span>Sadece tasarımı kaldır <span className="text-gray-400">(marka etiketi, renk adı yazısı vb. kalsın)</span></span>
              </label>
              <label className="mb-1.5 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={keepShirt} onChange={(e) => setKeepShirt(e.target.checked)} /> Tişört rengini/kumaşını koru</label>
              <label className="mb-2 flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={cleanBg} onChange={(e) => setCleanBg(e.target.checked)} /> Arka planı sadeleştir</label>
              <textarea value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Ek talimat (opsiyonel)"
                className="w-full rounded border border-gray-200 px-2 py-1 text-xs" rows={2} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">AI Modeli</label>
              <select value={effectiveModel} onChange={(e) => setModel(e.target.value)} className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
              </select>
            </div>
            <button onClick={generate} disabled={busy || selected.size === 0 || !effectiveModel}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Üretiliyor…" : `Boş Mockup Üret (${selected.size})`}
            </button>

          </div>
          </div>
          {results.length > 0 && (
            <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-emerald-600">✓ {results.filter((r) => r.imageUrl).length} boş mockup üretildi (havuza kaydedildi)</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {created.map((m) => (
                  <GeneratedMockupCard
                    key={m.id}
                    mockup={m}
                    productTypes={productTypes}
                    folderOptions={folderOptions}
                    vars={vars}
                    onDownload={() => download(m.imageUrl)}
                    onEditArea={() => setAreaMockup(m)}
                    onChanged={(next) => setCreated((prev) => prev.map((x) => (x.id === next.id ? next : x)))}
                  />
                ))}
                {/* Kayit cekilemediyse ya da hata olduysa sade gorunum */}
                {created.length === 0 && results.map((r, i) => (
                  <div key={i} className="rounded-lg border border-gray-200 p-1">
                    {r.imageUrl ? (
                    <>
                      <img src={abs(r.imageUrl)} alt="" className="h-24 w-full rounded object-cover" />
                      <button onClick={() => download(r.imageUrl!)} className="mt-1 flex w-full items-center justify-center gap-1 rounded bg-gray-900 py-1 text-[10px] text-white hover:bg-gray-800"><Download className="h-3 w-3" /> İndir</button>
                    </>
                    ) : (
                    <p className="p-2 text-[10px] text-red-500">{r.error || "Hata"}</p>
                    )}
                  </div>
                ))}
                {results.filter((r) => r.error).map((r, i) => (
                  <p key={`e${i}`} className="rounded-lg border border-red-200 bg-red-50 p-2 text-[10px] text-red-600">{r.error}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tasarim alani editoru — kendi tam ekran katmanini acar */}
      {areaMockup && (
        <div onClick={(e) => e.stopPropagation()}>
          <DesignAreaEditor
            mockup={areaMockup}
            onSave={(areas) => saveAreas(areaMockup.id, areas)}
            onCancel={() => setAreaMockup(null)}
          />
        </div>
      )}
    </div>
  );
}

// Uretilen bos mockup icin duzenlenebilir kart: isim, urun tipi, marka/model/renk,
// satan mockup isareti. Havuza zaten kaydedildi; buradaki degisiklikler dogrudan
// mockup kaydina yazilir, ayri bir sayfaya gitmeye gerek kalmaz.
function GeneratedMockupCard({
  mockup,
  productTypes,
  folderOptions,
  vars,
  onDownload,
  onEditArea,
  onChanged,
}: {
  mockup: CustomMockup;
  productTypes: Array<{ key: string; label: string }>;
  folderOptions: Array<{ id: string; label: string }>;
  vars: ReturnType<typeof useSupplierVariations>;
  onDownload: () => void;
  onEditArea: () => void;
  onChanged: (m: CustomMockup) => void;
}) {
  const [label, setLabel] = useState(mockup.label);
  const [saving, setSaving] = useState(false);
  const areas = (mockup as unknown as { designAreas?: AreaRect[] }).designAreas;
  const areaCount = Array.isArray(areas) && areas.length > 0
    ? areas.length
    : mockup.designAreaW > 20 && mockup.designAreaH > 20 ? 1 : 0;

  async function patch(data: Record<string, unknown>) {
    setSaving(true);
    // Iyimser guncelleme — arayuz beklemesin
    onChanged({ ...mockup, ...(data as Partial<CustomMockup>) });
    try {
      await fetch(`${getBackendUrl()}/api/mockups/${mockup.id}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(data),
      });
    } catch { /* ag hatasi — kullanici tekrar deneyebilir */ }
    finally { setSaving(false); }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2">
      <div className="relative">
        <img src={abs(mockup.imageUrl)} alt={mockup.label} className="h-40 w-full rounded bg-gray-50 object-contain" />
        {/* Satan mockup: uretilen listenin ana gorseli olur */}
        <button
          onClick={() => patch({ isSelling: !mockup.isSelling })}
          title={mockup.isSelling ? "Satan mockup — kaldır" : "Satan mockup olarak işaretle"}
          className={`absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
            mockup.isSelling ? "border-amber-500 bg-amber-500 text-white" : "border-gray-300 bg-white/85 text-gray-400 hover:border-amber-400"
          }`}
        >
          <Star className={`h-3 w-3 ${mockup.isSelling ? "fill-current" : ""}`} />
        </button>
        {saving && (
          <span className="absolute left-1 top-1 rounded bg-white/90 p-0.5">
            <Loader2 className="h-3 w-3 animate-spin text-gray-500" />
          </span>
        )}
      </div>

      {/* Isim — dogrudan yazilabilir. Once tiklayip acilan gizli bir alandi,
          duzenlenebildigi anlasilmiyordu. Alandan cikinca kaydedilir. */}
      <div className="mt-1.5">
        <label className="mb-0.5 flex items-center gap-1 text-[9px] uppercase tracking-wider text-gray-400">
          <Pencil className="h-2.5 w-2.5" /> Mockup adı
        </label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { if (label.trim() && label !== mockup.label) patch({ label: label.trim() }); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") { setLabel(mockup.label); (e.target as HTMLInputElement).blur(); }
          }}
          className="w-full rounded border border-gray-200 px-1.5 py-1 text-[11px] font-medium text-gray-800 focus:border-indigo-300 focus:outline-none"
        />
      </div>

      {/* Klasor — uretimden sonra da tasinabilsin */}
      <select
        value={mockup.folderId ?? ""}
        onChange={(e) => patch({ folderId: e.target.value || null })}
        title="Kaydedileceği klasör"
        className="mt-1 w-full rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600"
      >
        <option value="">Klasörsüz (kök)</option>
        {folderOptions.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
      </select>

      {/* Urun tipi */}
      <select
        value={mockup.productTypeKey ?? ""}
        onChange={(e) => patch({ productTypeKey: e.target.value || null })}
        className={`mt-1 w-full rounded border px-1.5 py-0.5 text-[10px] ${
          mockup.productTypeKey ? "border-gray-200 text-gray-600" : "border-amber-300 bg-amber-50 text-amber-700"
        }`}
      >
        <option value="">Ürün tipi yok</option>
        {productTypes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>

      {/* Marka / model / renk — tedarikci katalogu varsa */}
      {vars.hasCatalog && (
        <div className="mt-1 space-y-1">
          <select
            value={mockup.brand ?? ""}
            onChange={(e) => patch({ brand: e.target.value || null, model: null, color: null })}
            className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600"
          >
            <option value="">Marka</option>
            {vars.brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            value={mockup.model ?? ""}
            onChange={(e) => patch({ model: e.target.value || null, color: null })}
            disabled={!mockup.brand}
            className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600 disabled:opacity-50"
          >
            <option value="">Model</option>
            {vars.modelsForBrand(mockup.brand ?? "").map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            value={mockup.color ?? ""}
            onChange={(e) => patch({ color: e.target.value || null })}
            disabled={!mockup.model}
            className="w-full rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600 disabled:opacity-50"
          >
            <option value="">Renk</option>
            {vars.colorsFor(mockup.brand ?? "", mockup.model ?? "").map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {/* Tasarim alani: uretilen mockup havuza girdi ama alani yoksa Studio'da kullanilamaz */}
      <button
        onClick={onEditArea}
        className={`mt-1.5 flex w-full items-center justify-center gap-1 rounded border py-1 text-[10px] font-medium ${
          areaCount > 0
            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
        }`}
      >
        <Crop className="h-3 w-3" /> {areaCount > 0 ? `Alan: ${areaCount} · Düzenle` : "Alan belirle"}
      </button>

      <div className="mt-1 flex items-center justify-between text-[9px] text-gray-400">
        <span>{mockup.imageWidth}×{mockup.imageHeight}</span>
        <span title="Etiket rengi teması">{mockup.theme === "dark" ? "Koyu" : "Açık"}</span>
      </div>

      <button onClick={onDownload} className="mt-1 flex w-full items-center justify-center gap-1 rounded bg-gray-900 py-1 text-[10px] text-white hover:bg-gray-800">
        <Download className="h-3 w-3" /> İndir
      </button>
    </div>
  );
}
